<template>
  <div class="integrated-sales" data-testid="integrated-sales-page">
    <header class="page-bar">
      <div>
        <div class="title-row">
          <h1>{{ $t('배달 매출 관리') }}</h1>
          <SalesBasisInfo basis="gross" class="basis-inline" />
        </div>
        <SettlementStatusBadge :badges="dashboard.badges" />
      </div>
      <div class="page-actions">
        <el-button size="small" icon="el-icon-data-analysis" @click="goComparison">{{ $t('플랫폼 비교') }}</el-button>
        <el-button size="small" icon="el-icon-document" @click="goDetail">{{ $t('거래 상세') }}</el-button>
        <el-button
          v-if="checkAuthActionAccess('IntegratedSalesAdmin')"
          data-testid="integrated-sales-ops"
          size="small"
          icon="el-icon-setting"
          @click="goOps"
          >{{ $t('운영') }}</el-button
        >
      </div>
    </header>

    <section class="filter-zone">
      <div class="preset-chips" role="group" :aria-label="$t('기간 선택')">
        <button
          v-for="preset in periodPresets"
          :key="preset.type"
          type="button"
          class="chip"
          :class="{ active: activePreset === preset.type }"
          :data-testid="`preset-${preset.type}`"
          @click="selectPreset(preset.type)">
          <i :class="preset.icon"></i>
          {{ $t(preset.label) }}
        </button>
      </div>

      <div class="filter-band">
        <el-button
          size="small"
          :type="showCompare ? 'primary' : 'default'"
          :icon="showCompare ? 'el-icon-arrow-up' : 'el-icon-s-operation'"
          data-testid="integrated-sales-detail-toggle"
          @click="toggleCompare"
          >{{ $t('상세') }}</el-button
        >
        <el-date-picker
          v-model="dateRange"
          size="small"
          type="daterange"
          value-format="yyyy-MM-dd"
          :range-separator="$t('~')"
          :start-placeholder="$t('시작일')"
          :end-placeholder="$t('종료일')"
          @change="onDateRangeChange">
        </el-date-picker>
        <el-select v-model="platform" size="small" clearable :placeholder="$t('플랫폼')" class="platform-select">
          <el-option
            v-for="p in deliveryPlatformFilters"
            :key="p"
            :label="platformDisplayName(p)"
            :value="p"></el-option>
        </el-select>
        <el-button
          v-if="checkAuthActionAccess('IntegratedSalesView')"
          data-testid="integrated-sales-search"
          type="primary"
          size="small"
          icon="el-icon-search"
          @click="loadDashboard"
          >{{ $t('검색') }}</el-button
        >
      </div>

      <transition name="slide">
        <div v-if="showCompare" class="compare-panel" data-testid="integrated-sales-compare-panel">
          <el-checkbox v-model="compareMode" @change="onCompareModeChange">
            <i class="el-icon-sort"></i>
            {{ $t('기간 비교') }}
          </el-checkbox>
          <div v-if="compareMode" class="compare-controls">
            <el-radio-group v-model="compareType" size="small" @change="onCompareTypeChange">
              <el-radio-button v-for="c in compareTypes" :key="c.value" :label="c.value">{{
                $t(c.label)
              }}</el-radio-button>
            </el-radio-group>
            <el-date-picker
              v-if="compareType === 'CUSTOM'"
              v-model="compareDateRange"
              size="small"
              type="daterange"
              value-format="yyyy-MM-dd"
              :range-separator="$t('~')"
              :start-placeholder="$t('시작일')"
              :end-placeholder="$t('종료일')"
              @change="loadDashboard">
            </el-date-picker>
            <div v-if="compareRange" class="compare-range-info">
              <span class="current">{{ $t('조회') }}: {{ dateRange[0] }} ~ {{ dateRange[1] }}</span>
              <span class="compare">{{ $t('비교') }}: {{ compareRange[0] }} ~ {{ compareRange[1] }}</span>
            </div>
          </div>
        </div>
      </transition>
    </section>

    <el-alert v-if="errorMessage" type="error" :closable="false" show-icon class="state-alert">
      <span slot="title">{{ errorMessage }}</span>
    </el-alert>

    <div v-if="loading" class="state-panel" aria-busy="true">
      <i class="el-icon-loading"></i>
      <span>{{ $t('정산 데이터를 불러오는 중입니다') }}</span>
    </div>

    <template v-else>
      <SettlementSummaryStrip
        :summary="dashboard.summary"
        :deductions="dashboard.deductions"
        :savings="dashboard.savings"
        @open-savings="savingsDialogVisible = true" />

      <section
        v-if="compareMode && compareSummary"
        class="compare-summary"
        data-testid="integrated-sales-compare-summary">
        <div v-for="metric in compareMetrics" :key="metric.key" class="compare-card">
          <div class="compare-card-header">{{ metric.label }}</div>
          <div class="current-value">{{ formatCurrency(metric.current) }}</div>
          <div class="compare-value">
            <span class="label">{{ $t('비교') }}:</span>
            {{ formatCurrency(metric.compare) }}
          </div>
          <div class="diff-value" :class="diffClass(metric.current, metric.compare)">
            <i :class="diffIcon(metric.current, metric.compare)"></i>
            {{ diffPercent(metric.current, metric.compare) }}
          </div>
        </div>
      </section>

      <div v-if="isEmpty" class="state-panel empty">
        <i class="el-icon-folder-opened"></i>
        <span>{{ $t('선택한 기간에 배달 정산 데이터가 없습니다') }}</span>
      </div>

      <section v-else class="content-grid">
        <SettlementPlatformChart
          :platforms="dashboard.platforms"
          @select-platform="selectPlatform"
          @drilldown-platform="goDetailForPlatform" />
        <SettlementDeductionPanel
          :deductions="dashboard.deductions"
          :platform="platform"
          :deduction-sources="dashboard.platforms"
          :expected-settlement="dashboard.summary.expected_settlement || 0"
          :order-amount="dashboard.summary.order_amount || 0"
          :estimated-fee="dashboard.summary.estimated_fee || 0" />
      </section>

      <section class="insight-strip" aria-label="정산 인사이트">
        <div class="insight-item">
          <span>{{ $t('광고비 비율') }}</span>
          <strong :class="{ warning: adCostRate > 8 }">{{ adCostRate }}%</strong>
        </div>
        <div class="insight-item">
          <span>{{ $t('고객할인 비율') }}</span>
          <strong :class="{ warning: storeDiscountRate > 10 }">{{ storeDiscountRate }}%</strong>
        </div>
      </section>
    </template>

    <SettlementSavingsDialog
      :visible.sync="savingsDialogVisible"
      :value="Number(dashboard.savings.estimatedOrdertalkSavings || 0)" />
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import { util } from '@/mixins/utilMixin'
import { DELIVERY_PLATFORM_FILTERS, platformDisplayName } from '@/utils/deliveryPlatform'
import {
  PERIOD_PRESETS,
  COMPARE_TYPES,
  getDateRangeForPeriod,
  computeCompareRange,
  diffPercent,
  diffClass,
  diffIcon,
} from '@/utils/periodFilter'
import SettlementDeductionPanel from '@/components/integratedSales/SettlementDeductionPanel.vue'
import SettlementPlatformChart from '@/components/integratedSales/SettlementPlatformChart.vue'
import SettlementSavingsDialog from '@/components/integratedSales/SettlementSavingsDialog.vue'
import SettlementStatusBadge from '@/components/integratedSales/SettlementStatusBadge.vue'
import SettlementSummaryStrip from '@/components/integratedSales/SettlementSummaryStrip.vue'
import SalesBasisInfo from '@/components/SalesBasisInfo.vue'

export default {
  mixins: [mainVueMixin],
  components: {
    SettlementDeductionPanel,
    SettlementPlatformChart,
    SettlementSavingsDialog,
    SettlementStatusBadge,
    SettlementSummaryStrip,
    SalesBasisInfo,
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      dateRange: [util.getDate('yyyy-MM-dd', util.dateAdd('d ', -30, new Date())), util.getNow('yyyy-MM-dd')],
      platform: '',
      deliveryPlatformFilters: DELIVERY_PLATFORM_FILTERS,
      periodPresets: PERIOD_PRESETS,
      compareTypes: COMPARE_TYPES,
      activePreset: null,
      showCompare: false,
      compareMode: false,
      compareType: 'PREV_YEAR',
      compareDateRange: null,
      compareSummary: null,
      savingsDialogVisible: false,
      dashboard: {
        summary: {},
        platforms: [],
        daily: [],
        deductions: {},
        details: [],
        badges: {},
        savings: {},
      },
    }
  },
  computed: {
    isEmpty() {
      return Number(this.dashboard.summary.transaction_count || 0) === 0
    },
    compareRange() {
      if (!this.compareMode) return null
      return computeCompareRange(this.dateRange, this.compareType, this.compareDateRange)
    },
    compareMetrics() {
      const current = this.dashboard.summary || {}
      const compare = this.compareSummary || {}
      return [
        {
          key: 'sales',
          label: this.$t('기준매출(정산)'),
          current: Number(current.order_amount || 0),
          compare: Number(compare.order_amount || 0),
        },
        {
          key: 'fees',
          label: this.$t('수수료 예상'),
          current: Number(current.deduction_amount || 0) + Number(current.estimated_fee || 0),
          compare: Number(compare.deduction_amount || 0) + Number(compare.estimated_fee || 0),
        },
        {
          key: 'expected_settlement',
          label: this.$t('정산 예상'),
          current: Number(current.expected_settlement || 0),
          compare: Number(compare.expected_settlement || 0),
        },
      ]
    },
    adCostRate() {
      const sales = Number(this.dashboard.summary.order_amount || 0)
      if (!sales) return 0
      return ((Number(this.dashboard.deductions.ad_cost || 0) / sales) * 100).toFixed(1)
    },
    storeDiscountRate() {
      const sales = Number(this.dashboard.summary.order_amount || 0)
      if (!sales) return 0
      return ((Number(this.dashboard.deductions.store_discount || 0) / sales) * 100).toFixed(1)
    },
  },
  created() {
    this.loadDashboard()
  },
  methods: {
    platformDisplayName,
    diffPercent,
    diffClass,
    diffIcon,
    formatCurrency(value) {
      return `${Number(value || 0).toLocaleString()}원`
    },
    buildParams() {
      return {
        startDate: this.dateRange?.[0],
        endDate: this.dateRange?.[1],
        platform: this.platform || undefined,
      }
    },
    async loadDashboard() {
      this.loading = true
      this.errorMessage = ''
      try {
        const outcome = await this.mainVue.$axios.search(
          '/api/statistics/integrated-sales/summary',
          this.buildParams(),
          (result) => {
            this.dashboard = {
              summary: result.summary || {},
              platforms: result.platforms || [],
              daily: result.daily || [],
              deductions: result.deductions || {},
              details: result.details || [],
              badges: result.badges || {},
              savings: result.savings || {},
            }
          },
          false
        )
        // 요청 실패는 헬퍼가 값으로 알린다 — 비교 조회로 넘어가지 않고 아래 catch 한 곳이 안내한다
        if (!outcome.ok) throw outcome.error
        await this.loadCompareSummary()
      } catch (error) {
        this.errorMessage = this.$t('정산 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.')
      } finally {
        this.loading = false
      }
    },
    async loadCompareSummary() {
      const range = this.compareRange
      if (!this.compareMode || !range) {
        this.compareSummary = null
        return
      }
      const outcome = await this.mainVue.$axios.search(
        '/api/statistics/integrated-sales/summary',
        { startDate: range[0], endDate: range[1], platform: this.platform || undefined },
        (result) => {
          this.compareSummary = result.summary || {}
        },
        false
      )
      // 부르는 쪽(`loadDashboard`)의 catch 가 안내한다 — 요청 실패를 그 자리로 올린다
      if (!outcome.ok) throw outcome.error
    },
    selectPreset(type) {
      this.activePreset = type
      const { startDate, endDate } = getDateRangeForPeriod(type)
      this.dateRange = [startDate, endDate]
      this.loadDashboard()
    },
    onDateRangeChange() {
      // 날짜를 직접 바꾸면 프리셋 선택 해제. 조회는 검색 버튼으로.
      this.activePreset = null
    },
    toggleCompare() {
      this.showCompare = !this.showCompare
    },
    onCompareModeChange() {
      if (!this.compareMode) {
        this.compareSummary = null
        return
      }
      this.loadDashboard()
    },
    onCompareTypeChange() {
      if (this.compareType !== 'CUSTOM' || (this.compareDateRange && this.compareDateRange.length === 2)) {
        this.loadDashboard()
      } else {
        // CUSTOM 으로 바꿨지만 비교 날짜 미선택 → 조회를 건너뛴다. 이때 이전 비교 타입의
        // compareSummary 를 비우지 않으면 빈 커스텀 범위에 옛 비교 카드 숫자가 잔존한다.
        this.compareSummary = null
      }
    },
    selectPlatform(platform) {
      this.platform = platform
      this.loadDashboard()
    },
    goDetail() {
      this.$router.push({ path: '/v/business/integrated-sales/detail', query: this.buildParams() }).catch(() => {})
    },
    goDetailForPlatform(platform) {
      this.$router
        .push({ path: '/v/business/integrated-sales/detail', query: { ...this.buildParams(), platform } })
        .catch(() => {})
    },
    goComparison() {
      this.$router.push({ path: '/v/business/integrated-sales/comparison', query: this.buildParams() }).catch(() => {})
    },
    goOps() {
      this.$router.push('/v/business/integrated-sales/ops').catch(() => {})
    },
  },
}
</script>

<style scoped>
.integrated-sales {
  min-height: calc(100vh - 120px);
  padding: 16px;
  background: var(--color-bg-page);
}

.page-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 14px;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.title-row h1 {
  margin: 0;
}

h1 {
  margin: 0 0 10px;
  color: var(--color-text-primary);
  font-size: 18px;
  font-weight: 700;
}

.page-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.filter-zone {
  margin-bottom: 14px;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  box-shadow: var(--shadow-card);
}

.preset-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border-light);
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-bg-page);
  color: var(--color-text-regular);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.chip:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.chip.active {
  border-color: var(--color-primary);
  background: var(--color-primary);
  color: #fff;
}

.chip i {
  font-size: 14px;
}

.filter-band {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.platform-select {
  width: 150px;
}

.compare-panel {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--color-border-light);
}

.compare-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.compare-range-info {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
}

.compare-range-info .current {
  color: var(--color-primary);
  font-weight: 600;
}

.compare-range-info .compare {
  color: var(--color-text-secondary);
}

.slide-enter-active,
.slide-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.compare-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.compare-card {
  padding: 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  box-shadow: var(--shadow-card);
  text-align: center;
}

.compare-card-header {
  margin-bottom: 10px;
  color: var(--color-text-secondary);
  font-size: 13px;
}

.compare-card .current-value {
  margin-bottom: 6px;
  color: var(--color-text-primary);
  font-size: 22px;
  font-weight: 700;
}

.compare-card .compare-value {
  margin-bottom: 6px;
  color: var(--color-text-secondary);
  font-size: 12px;
}

.compare-card .compare-value .label {
  margin-right: 4px;
}

.compare-card .diff-value {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
}

.diff-value.positive {
  color: var(--color-success);
}

.diff-value.negative {
  color: var(--color-danger);
}

.diff-value.neutral {
  color: var(--color-text-secondary);
}

.state-alert {
  margin-bottom: 12px;
}

.state-panel {
  display: flex;
  min-height: 160px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 14px 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-regular);
  font-size: 14px;
}

.state-panel i {
  color: var(--color-primary);
  font-size: 20px;
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  margin-top: 12px;
}

.content-grid > * {
  min-width: 0;
}

.insight-strip {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.insight-item {
  display: flex;
  min-height: 58px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-regular);
}

.insight-item strong {
  color: var(--color-text-primary);
  font-size: 18px;
}

.insight-item strong.warning {
  color: var(--color-warning);
}

@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .integrated-sales {
    padding: 10px;
  }

  .page-bar {
    flex-direction: column;
  }

  .page-actions,
  .filter-band .el-date-editor,
  .platform-select {
    width: 100%;
  }

  .page-actions .el-button {
    flex: 1;
  }

  .insight-strip {
    grid-template-columns: 1fr;
  }

  .compare-summary {
    grid-template-columns: 1fr;
  }
}
</style>
